// ---------------------------------------------------------------
// useEvents.js — Suscripción en tiempo real a la colección `events`
// filtrada SIEMPRE por el userId del usuario logueado, más las
// operaciones CRUD (añadir, editar, eliminar).
// ---------------------------------------------------------------
import { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

export function useEvents(user) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return undefined;

    // Filtro estricto por userId: cada usuario sólo ve su calendario
    const q = query(collection(db, "events"), where("userId", "==", user.uid));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) =>
          a.date === b.date
            ? (a.startTime || "").localeCompare(b.startTime || "")
            : a.date.localeCompare(b.date)
        );
        setEvents(docs);
        setLoading(false);
      },
      (error) => {
        console.error("Error leyendo eventos:", error);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [user]);

  /* Mapa fecha -> eventos, para pintar la cuadrícula del mes rápido.
     Los eventos que se repiten se DESPLIEGAN aquí: en Firestore hay un
     único documento con su `repeat`, y las repeticiones se calculan al
     vuelo. Guardar una copia por ocurrencia habría llenado la base de
     datos y habría hecho imposible cambiar la serie entera.

     La ventana es de un año hacia atrás y dos hacia delante: cubre de
     sobra lo que se puede llegar a mirar en el calendario y evita que
     el mapa crezca sin fin con un evento anual de hace diez años. */
  const eventsByDate = useMemo(() => {
    const map = {};
    const hoy = new Date();
    const desde = new Date(hoy.getFullYear() - 1, 0, 1);
    const hasta = new Date(hoy.getFullYear() + 2, 11, 31);
    const iso = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;

    const meter = (fecha, ev) => (map[fecha] = map[fecha] || []).push(ev);

    for (const ev of events) {
      const repite = ev.repeat && ev.repeat !== "once";
      if (!repite) {
        meter(ev.date, ev);
        continue;
      }
      const inicio = new Date(`${ev.date}T12:00:00`);
      const dia = inicio.getDate();
      const mes = inicio.getMonth();

      if (ev.repeat === "weekly") {
        const cursor = new Date(inicio);
        // Retroceder hasta el principio de la ventana en saltos de 7
        while (cursor > desde) cursor.setDate(cursor.getDate() - 7);
        while (cursor < desde) cursor.setDate(cursor.getDate() + 7);
        for (; cursor <= hasta; cursor.setDate(cursor.getDate() + 7)) {
          if (cursor >= inicio) meter(iso(cursor), { ...ev, date: iso(cursor) });
        }
      } else if (ev.repeat === "monthly") {
        for (let a = desde.getFullYear(); a <= hasta.getFullYear(); a++) {
          for (let m = 0; m < 12; m++) {
            const d = new Date(a, m, dia);
            // Un evento del 31 no existe en febrero: se salta el mes en
            // vez de correrlo al 1 de marzo, que no es lo que nadie
            // espera de "todos los días 31".
            if (d.getDate() !== dia) continue;
            if (d >= inicio && d >= desde && d <= hasta) {
              meter(iso(d), { ...ev, date: iso(d) });
            }
          }
        }
      } else if (ev.repeat === "yearly") {
        for (let a = desde.getFullYear(); a <= hasta.getFullYear(); a++) {
          const d = new Date(a, mes, dia);
          if (d.getDate() !== dia) continue; // 29 de febrero
          if (d >= inicio && d <= hasta) meter(iso(d), { ...ev, date: iso(d) });
        }
      }
    }

    // Dentro de cada día, por hora de inicio
    for (const lista of Object.values(map)) {
      lista.sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));
    }
    return map;
  }, [events]);

  const addEvent = (data) =>
    addDoc(collection(db, "events"), {
      ...data,
      userId: user.uid,
      createdAt: serverTimestamp(),
    });

  const updateEvent = (id, data) => updateDoc(doc(db, "events", id), data);

  const deleteEvent = (id) => deleteDoc(doc(db, "events", id));

  return { events, eventsByDate, loading, addEvent, updateEvent, deleteEvent };
}

/**
 * Sólo los eventos de UN día (para los recordatorios de la campana):
 * evita descargar todo el calendario del usuario.
 */
export function useDayEvents(user, dateISO) {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!user || !dateISO) return undefined;
    const q = query(
      collection(db, "events"),
      where("userId", "==", user.uid),
      where("date", "==", dateISO)
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (error) => {
        console.error("Error leyendo eventos del día:", error);
        setEvents([]);
      }
    );
    return unsubscribe;
  }, [user, dateISO]);

  return events;
}
